import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  InputAdornment,
  TextField,
} from "@mui/material";
import FormTextField, { fieldShellSx } from "../../../../../../../core/ui/FormTextField";
import Button from "../../../../../../../core/ui/Button";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { CalenderIcon, DeleteIcon } from "../../../../../../../core/global/Icons";

dayjs.extend(isSameOrAfter);

const REPORTING_PERIOD_OPTIONS = [
  { value: "Calendar", label: "Calendar" },
  { value: "Fiscal", label: "Fiscal" },
];

const DATE_FIELD_SX = {
  width: "361px",
  ...fieldShellSx(),
};

export default function EngagementsStep({
  form,
  handleEngagementChange,
  handleAddEngagement,
  handleRemoveEngagement,
  errors = {},
}) {
  const [openDatePickers, setOpenDatePickers] = useState({});

  const setDatePickerOpen = (key, value) => {
    setOpenDatePickers((prev) => ({ ...prev, [key]: value }));
  };

  const handleCoverageFromChange = (engagement, newValue) => {
    const formattedFrom = newValue ? newValue.format("YYYY-MM-DD") : "";
    handleEngagementChange(engagement.id, "coverageFrom", formattedFrom);
    if (engagement.reportingPeriod === "Fiscal" && newValue) {
      const autoTo = newValue.add(1, "year").format("YYYY-MM-DD");
      handleEngagementChange(engagement.id, "coverageTo", autoTo);
    } else if (engagement.reportingPeriod === "Calendar") {
      if (
        engagement.coverageTo &&
        newValue &&
        dayjs(engagement.coverageTo).isBefore(newValue)
      ) {
        handleEngagementChange(engagement.id, "coverageTo", "");
      }
    }
    setDatePickerOpen(`coverageFrom-${engagement.id}`, false);
  };

  const handleCoverageToChange = (engagement, newValue) => {
    handleEngagementChange(
      engagement.id,
      "coverageTo",
      newValue ? newValue.format("YYYY-MM-DD") : ""
    );
    setDatePickerOpen(`coverageTo-${engagement.id}`, false);
  };

  const handleReportingPeriodChange = (engagement, value) => {
    handleEngagementChange(engagement.id, "reportingPeriod", value);
    handleEngagementChange(engagement.id, "coverageFrom", "");
    handleEngagementChange(engagement.id, "coverageTo", "");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {form.engagements.map((engagement, index) => {
        const engErr = (errors.engagements && errors.engagements[index]) || {};
        const isFiscal = engagement.reportingPeriod === "Fiscal";
        const isCalendar = engagement.reportingPeriod === "Calendar";

        const coverageToMinDate =
          isCalendar && engagement.coverageFrom
            ? dayjs(engagement.coverageFrom)
            : undefined;

        const fiscalToValue =
          isFiscal && engagement.coverageFrom
            ? dayjs(engagement.coverageFrom).add(1, "year")
            : null;

        return (
          <Box
            key={engagement.id}
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              boxShadow:
                "0px 2px 4px rgba(0, 0, 0, 0.08), 0px 0px 6px rgba(0, 0, 0, 0.02)",
              p: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#313131" }}>
                Engagement - {engagement.engagementType || "New Engagement"}
              </Typography>
            </Box>

            <Grid container sx={{ maxWidth: "746px", gap: "24px" }}>
              <Grid item xs={12}>
                <FormTextField
                  label="Engagement Type"
                  required
                  value={engagement.engagementType}
                  maxwidth="361px"
                  onChange={(e) =>
                    handleEngagementChange(engagement.id, "engagementType", e.target.value)
                  }
                  placeholder="ICPA Audit"
                  error={!!engErr.engagementType}
                  errorMessage={engErr.engagementType}
                />
              </Grid>

              <Grid item xs={12} sm="auto">
                <FormTextField
                  label="Reporting Period"
                  required
                  value={engagement.reportingPeriod}
                  maxwidth="361px"
                  onChange={(e) =>
                    handleReportingPeriodChange(engagement, e.target.value)
                  }
                  select
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) return <span style={{ color: "#999" }}>Calendar</span>;
                      return selected;
                    },
                  }}
                  error={!!engErr.reportingPeriod}
                  errorMessage={engErr.reportingPeriod}
                >
                  {REPORTING_PERIOD_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </FormTextField>
              </Grid>

              {/* Ending Date */}
              <Grid item xs={12} sm="auto">
                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#313131", mb: 1 }}>
                  Ending Date <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    open={!!openDatePickers[`endDate-${engagement.id}`]}
                    value={engagement.endDate ? dayjs(engagement.endDate) : null}
                    onChange={(newValue) => {
                      handleEngagementChange(
                        engagement.id,
                        "endDate",
                        newValue ? newValue.format("YYYY-MM-DD") : ""
                      );
                      setDatePickerOpen(`endDate-${engagement.id}`, false);
                    }}
                    onClose={() => setDatePickerOpen(`endDate-${engagement.id}`, false)}
                    inputFormat="MM/DD/YYYY"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="01/28/2026"
                        onClick={() => setDatePickerOpen(`endDate-${engagement.id}`, true)}
                        sx={DATE_FIELD_SX}
                        error={!!engErr.endDate}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{ cursor: "pointer" }}
                              onClick={() => setDatePickerOpen(`endDate-${engagement.id}`, true)}
                            >
                              <CalenderIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
                {engErr.endDate && (
                  <Typography
                    sx={{
                      color: "#D14343",
                      fontSize: "11px",
                      mt: 1,
                      lineHeight: 1,
                    }}
                  >
                    {engErr.endDate}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 500, color: "#313131" }}
                >
                  Engagement Coverage
                </Typography>
              </Grid>

              <Grid item xs={12} sm="auto">
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#313131",
                    mb: 1,
                  }}
                >
                  From <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    open={!!openDatePickers[`coverageFrom-${engagement.id}`]}
                    value={
                      engagement.coverageFrom
                        ? dayjs(engagement.coverageFrom)
                        : null
                    }
                    onChange={(newValue) =>
                      handleCoverageFromChange(engagement, newValue)
                    }
                    onClose={() =>
                      setDatePickerOpen(`coverageFrom-${engagement.id}`, false)
                    }
                    inputFormat="MM/DD/YYYY"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="01/01/2026"
                        onClick={() =>
                          setDatePickerOpen(
                            `coverageFrom-${engagement.id}`,
                            true,
                          )
                        }
                        sx={DATE_FIELD_SX}
                        error={!!engErr.coverageFrom}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{ cursor: "pointer" }}
                              onClick={() =>
                                setDatePickerOpen(
                                  `coverageFrom-${engagement.id}`,
                                  true,
                                )
                              }
                            >
                              <CalenderIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
                {engErr.coverageFrom && (
                  <Typography
                    sx={{ color: "#D14343", fontSize: "11px", mt: 1 }}
                  >
                    {engErr.coverageFrom}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm="auto">
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#313131",
                    mb: 1,
                  }}
                >
                  To <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    open={
                      isFiscal
                        ? false
                        : !!openDatePickers[`coverageTo-${engagement.id}`]
                    }
                    value={
                      isFiscal
                        ? fiscalToValue
                        : engagement.coverageTo
                          ? dayjs(engagement.coverageTo)
                          : null
                    }
                    minDate={coverageToMinDate}
                    disabled={isFiscal}
                    onChange={(newValue) =>
                      handleCoverageToChange(engagement, newValue)
                    }
                    onClose={() =>
                      setDatePickerOpen(`coverageTo-${engagement.id}`, false)
                    }
                    inputFormat="MM/DD/YYYY"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="12/31/2026"
                        onClick={() => {
                          if (!isFiscal) {
                            setDatePickerOpen(
                              `coverageTo-${engagement.id}`,
                              true,
                            );
                          }
                        }}
                        sx={{
                          ...DATE_FIELD_SX,
                          ...(isFiscal && {
                            "& .MuiInputBase-root": {
                              backgroundColor: "#F3F4F6",
                              cursor: "not-allowed",
                            },
                          }),
                        }}
                        error={!isFiscal && !!engErr.coverageTo}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{
                                cursor: isFiscal ? "not-allowed" : "pointer",
                              }}
                              onClick={() => {
                                if (!isFiscal) {
                                  setDatePickerOpen(
                                    `coverageTo-${engagement.id}`,
                                    true,
                                  );
                                }
                              }}
                            >
                              <CalenderIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
                {!isFiscal && engErr.coverageTo && (
                  <Typography
                    sx={{
                      color: "#D14343",
                      fontSize: "11px",
                      mt: 1,
                      lineHeight: 1,
                    }}
                  >
                    {engErr.coverageTo}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              sx={{ mt: 5, width: "130px" }}
              onClick={() => handleRemoveEngagement(engagement.id)}
              disabled={form.engagements.length === 1}
            >
              Remove
            </Button>
          </Box>
        );
      })}
    </Box>
  );
}